import React from 'react';

import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { ArrowRight, Pencil, Printer } from 'lucide-react';

import PageHeader from '@/components/shared/PageHeader';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { Button } from '@/components/ui/button';

import { accountingApi, type AccountingDocumentRecord } from '@/lib/api/accounting';

import {

  accountingDocumentHref,

  filterDocumentRowsForProject,

  parseProjectScopeId,

  sumDocumentRowTotals,

} from '@/lib/accountingDocumentProjectScope';

import { formatRial } from '@/lib/formatMoney';
import CounterpartySideBadge from '@/components/document-entry/CounterpartySideBadge';
import { counterpartyKeyFromRow } from '@/lib/counterpartySide';



const AccountingDocumentDetailsPage: React.FC = () => {

  const { id } = useParams<{ id: string }>();

  const [searchParams] = useSearchParams();

  const scopeProjectId = parseProjectScopeId(searchParams.get('projectId'));

  const [doc, setDoc] = React.useState<AccountingDocumentRecord | null>(null);

  const [projectNameMap, setProjectNameMap] = React.useState<Record<number, string>>({});

  const [accountTitleMap, setAccountTitleMap] = React.useState<Record<string, string>>({});

  const [beneficiaryNameMap, setBeneficiaryNameMap] = React.useState<Record<string, string>>({});

  const [loading, setLoading] = React.useState(true);



  React.useEffect(() => {

    if (!id) return;

    setLoading(true);

    accountingApi

      .accountingDocument(id)

      .then(setDoc)

      .catch(() => setDoc(null))

      .finally(() => setLoading(false));

  }, [id]);



  React.useEffect(() => {

    accountingApi

      .lookups()

      .then((data) => {

        const map: Record<number, string> = {};

        (data.projects || []).forEach((p) => {

          map[p.id] = p.label;

        });

        setProjectNameMap(map);

        const benMap: Record<string, string> = {};

        (data.beneficiaries || []).forEach((b) => {

          benMap[b.id] = b.name;

        });

        setBeneficiaryNameMap(benMap);

      })

      .catch(() => {

        setProjectNameMap({});

        setBeneficiaryNameMap({});

      });



    accountingApi

      .accountingHeadingsTree()

      .then((nodes) => {

        const map: Record<string, string> = {};

        const walk = (items: typeof nodes) => {

          items.forEach((n) => {

            map[n.code] = n.title;

            if (n.children?.length) walk(n.children);

          });

        };

        walk(nodes);

        setAccountTitleMap(map);

      })

      .catch(() => setAccountTitleMap({}));

  }, []);



  const allRows = doc?.rows || [];

  const displayRows = React.useMemo(

    () => filterDocumentRowsForProject(allRows, scopeProjectId),

    [allRows, scopeProjectId],

  );

  const scopedTotals = React.useMemo(() => sumDocumentRowTotals(displayRows), [displayRows]);

  const scopeProjectLabel = scopeProjectId ? projectNameMap[scopeProjectId] : null;



  if (!id) {

    return <Navigate to="/accounting-documents" replace />;

  }

  if (loading) {

    return <div className="p-4 text-sm text-slate-500">در حال دریافت اطلاعات سند...</div>;

  }

  if (!doc) {

    return <Navigate to="/accounting-documents" replace />;

  }



  const showScopedTotals = Boolean(scopeProjectId);

  const headerDebit = showScopedTotals ? scopedTotals.totalDebit : doc.totalDebit;

  const headerCredit = showScopedTotals ? scopedTotals.totalCredit : doc.totalCredit;



  return (

    <div className="w-full min-w-0 space-y-6">

      <PageHeader

        title={`جزئیات سند شماره ${doc.docNo}`}

        description={

          showScopedTotals && scopeProjectLabel

            ? `فقط ردیف‌های مربوط به پروژه: ${scopeProjectLabel}`

            : 'ریز اطلاعات سند ثبت‌شده'

        }

        actions={

          <div className="flex flex-wrap gap-2">

            <Button asChild variant="outline" className="gap-2">

              <Link to={accountingDocumentHref(doc.id, { projectId: scopeProjectId ?? undefined, edit: true })}>

                <Pencil className="h-4 w-4" />

                ویرایش سند

              </Link>

            </Button>

            <Button asChild variant="outline" className="gap-2">

              <Link to={accountingDocumentHref(doc.id, { projectId: scopeProjectId ?? undefined, print: true })}>

                <Printer className="h-4 w-4" />

                چاپ / PDF

              </Link>

            </Button>

            <Button asChild variant="outline" className="gap-2">

              <Link to={scopeProjectId ? `/projects/${scopeProjectId}/overview` : '/accounting-documents'}>

                <ArrowRight className="h-4 w-4" />

                {scopeProjectId ? 'بازگشت به اسناد پروژه' : 'بازگشت به لیست اسناد'}

              </Link>

            </Button>

          </div>

        }

      />



      {showScopedTotals ? (

        <p className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">

          نمایش <strong>{displayRows.length}</strong> ردیف از <strong>{allRows.length}</strong> ردیف سند — فقط

          ردیف‌هایی که پروژهٔ «{scopeProjectLabel || scopeProjectId}» دارند.

          {displayRows.length < allRows.length ? (

            <span className="mt-1 block text-xs text-sky-800">

              برای دیدن کل سند (شامل بستانکار پرسنل و سایر پروژه‌ها)، از{' '}

              <Link to={accountingDocumentHref(doc.id)} className="font-medium underline">

                نمایش کامل سند

              </Link>{' '}

              استفاده کنید.

            </span>

          ) : null}

        </p>

      ) : null}



      <Card>

        <CardHeader>

          <CardTitle className="text-base">مشخصات سند</CardTitle>

        </CardHeader>

        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-4">

          <div>

            <div className="text-xs text-slate-500">شماره سند</div>

            <div>{doc.docNo}</div>

          </div>

          <div>

            <div className="text-xs text-slate-500">تاریخ سند</div>

            <div>{doc.docDate}</div>

          </div>

          <div>

            <div className="text-xs text-slate-500">

              جمع بدهکار{showScopedTotals ? ' (این پروژه)' : ''}

            </div>

            <div className="font-mono">{formatRial(headerDebit)}</div>

            {showScopedTotals && doc.totalDebit !== headerDebit ? (

              <div className="text-[10px] text-slate-500">کل سند: {formatRial(doc.totalDebit)}</div>

            ) : null}

          </div>

          <div>

            <div className="text-xs text-slate-500">

              جمع بستانکار{showScopedTotals ? ' (این پروژه)' : ''}

            </div>

            <div className="font-mono">{formatRial(headerCredit)}</div>

            {showScopedTotals && doc.totalCredit !== headerCredit ? (

              <div className="text-[10px] text-slate-500">کل سند: {formatRial(doc.totalCredit)}</div>

            ) : null}

          </div>

          <div className="md:col-span-4">

            <div className="text-xs text-slate-500">شرح عملیات</div>

            <div>{doc.operationDescription || '-'}</div>

          </div>

        </CardContent>

      </Card>



      <Card>

        <CardHeader>

          <CardTitle className="text-base">

            ردیف‌های سند

            {showScopedTotals && scopeProjectLabel ? ` — ${scopeProjectLabel}` : ''}

          </CardTitle>

        </CardHeader>

        <CardContent>

          <div className="overflow-x-auto rounded-md border">

            <table className="w-full min-w-[1050px] text-sm">

              <thead>

                <tr className="bg-slate-100 text-slate-700">

                  <th className="border px-2 py-2 text-right">عنوان حساب</th>

                  <th className="border px-2 py-2 text-right">طرف شخص/شرکت</th>

                  <th className="border px-2 py-2 text-right">نقش طرف</th>

                  <th className="border px-2 py-2 text-right">نام پروژه</th>

                  <th className="border px-2 py-2 text-right">شرح ردیف</th>

                  <th className="border px-2 py-2 text-right">بدهکار</th>

                  <th className="border px-2 py-2 text-right">بستانکار</th>

                </tr>

              </thead>

              <tbody>

                {displayRows.length ? (

                  displayRows.map((r) => {

                    const cpKey = counterpartyKeyFromRow(r.counterpartyType, r.counterpartyId);

                    const partyName =

                      (r.counterpartyName && r.counterpartyName.trim()) ||

                      (cpKey ? beneficiaryNameMap[cpKey] : '') ||

                      '—';

                    return (

                    <tr key={r.id}>

                      <td className="border px-2 py-2">

                        {accountTitleMap[r.accountCode] || r.accountCode || '-'}

                      </td>

                      <td className="border px-2 py-2 font-medium text-slate-800">{partyName}</td>

                      <td className="border px-2 py-2">

                        {cpKey || r.counterpartySide ? (

                          <CounterpartySideBadge

                            debit={Number(r.debit || 0)}

                            credit={Number(r.credit || 0)}

                            side={r.counterpartySide ?? undefined}

                          />

                        ) : (

                          <span className="text-slate-400">—</span>

                        )}

                      </td>

                      <td className="border px-2 py-2">

                        {(r.projectId && projectNameMap[r.projectId]) || '—'}

                      </td>

                      <td className="border px-2 py-2 text-slate-600">{r.accountTitle || '—'}</td>

                      <td className="border px-2 py-2 font-mono text-rose-800">

                        {Number(r.debit || 0) > 0 ? formatRial(Number(r.debit)) : '—'}

                      </td>

                      <td className="border px-2 py-2 font-mono text-emerald-800">

                        {Number(r.credit || 0) > 0 ? formatRial(Number(r.credit)) : '—'}

                      </td>

                    </tr>

                    );

                  })

                ) : (

                  <tr>

                    <td colSpan={7} className="border px-2 py-8 text-center text-slate-500">

                      {showScopedTotals

                        ? 'ردیفی با پروژهٔ انتخاب‌شده در این سند نیست.'

                        : 'ردیفی ثبت نشده است.'}

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </CardContent>

      </Card>

    </div>

  );

};



export default AccountingDocumentDetailsPage;


